import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import imageService, { Image } from '../../services/image.service';

const SearchImages: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  const [filteredImages, setFilteredImages] = useState<Image[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load public images
  useEffect(() => {
    const loadImages = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await imageService.getAvailableImages();
        setImages(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((image: Image) => {
          image.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading images:', err);
        setError('Erreur lors du chargement des images');
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  // Filter images
  useEffect(() => {
    let filtered = [...images];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (image) =>
          image.title?.toLowerCase().includes(query) ||
          image.description?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((image) =>
        selectedTags.every((tag) => image.tags?.includes(tag))
      );
    }

    setFilteredImages(filtered);
  }, [searchQuery, selectedTags, images]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="images"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredImages.length} image{filteredImages.length > 1 ? 's' : ''} trouvée{filteredImages.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredImages.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card>
                    {image.thumbnail_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={image.thumbnail_url}
                        alt={image.title}
                        sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {image.title}
                      </Typography>

                      {image.created_by_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Par {image.created_by_name}
                        </Typography>
                      )}

                      {image.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {image.description}
                        </Typography>
                      )}

                      {image.width && image.height && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {image.width} × {image.height} px
                        </Typography>
                      )}

                      {image.tags && image.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {image.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredImages.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucune image ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchImages;
